import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IRegisterAdmin } from '../../brand/dto/IRegister';
import { useRecaptcha } from '../../../../shared/hooks/useRecaptcha';
import { Col, Row } from 'react-bootstrap';
import { Checkbox, TextField } from '@mui/material';
import InputPassword from '../../../../shared/components/input/InputPassword';
import { PasswordVerifySteps } from '../../../../shared/PasswordVerifySteps';
import { StepperButtons } from '../../brand/components/StepperButtons';
import { AuthService } from '../../../service';
import { gtmService } from '../../../../utils/gtm.service';
import { ErrorToast, SuccessToast, WarningToast } from '../../../../utils/toasters';
import { IRegisterStepperProps } from '../../../../model/shared/IRegistration';
import { Form } from 'react-bootstrap';

const StepOne = ({ stepIdNumber, handleBackStep }: IRegisterStepperProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [state, setState] = useState<IRegisterAdmin>({
        email: '',
        password: '',
        isGeneralTermsAccepted: false,
        repeatPassword: '',
    });
    const { setToken, executeRecaptcha } = useRecaptcha();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const handleNextStep = async () => {
        if (!executeRecaptcha) {
            return;
        }
        const result = await executeRecaptcha('register');
        setToken(result);
        if (state?.isGeneralTermsAccepted) {
            setIsLoading(true);
            AuthService.registerGlobalDiscovery(state)
                .then(response => {
                    if (response) {
                        gtmService('discovery_lead_created');
                        navigate(
                            `/global-discovery/register/step/${stepIdNumber + 1}?token=${response?.verificationUUID}`
                        );
                        SuccessToast(t('auth.register.brand.step1.pleaseCheckEmail'));
                    }
                })
                .catch(error => ErrorToast(error))
                .finally(() => setIsLoading(false));
        } else {
            WarningToast(t('auth.register.brand.step1.termsNotSelected'));
        }
    };

    const changeHandler = (event: React.ChangeEvent<any>, isCheckbox = false) => {
        const { name, value, checked } = event.target;
        setState(prev => ({
            ...prev,
            [name]: isCheckbox ? checked : value,
        }));
    };

    return (
        <Form
            onSubmit={e => {
                e.preventDefault();
                handleNextStep();
            }}
        >
            <Row className="justify-content-center">
                <Col xs={12}>
                    <h3>{t('auth.register.brand.step1.adminAccount')}</h3>
                    <p className="text-muted mb-4">{t('auth.register.brand.step1.adminAccountDescription')}</p>
                </Col>

                <Col md={7}>
                    <TextField
                        label="E-mail"
                        variant="outlined"
                        size="small"
                        defaultValue={state?.email ?? ''}
                        required
                        type="email"
                        name="email"
                        onChange={changeHandler}
                        className="w-100 my-2"
                    />
                </Col>
                <Col md={7}>
                    <InputPassword
                        label={t('auth.register.brand.step1.password')}
                        name="password"
                        className="w-100 my-2"
                        onChange={changeHandler}
                        required
                    />
                </Col>
                <Col md={7}>
                    <InputPassword
                        label={t('auth.register.brand.step1.repeatPassword')}
                        name="repeatPassword"
                        className="w-100 my-2"
                        onChange={changeHandler}
                        required
                    />
                </Col>
                <Col md={7}>
                    <PasswordVerifySteps password={state.password ?? ''} />
                </Col>

                <Col md={7} className="my-2 d-flex align-items-center">
                    <Checkbox
                        defaultChecked={state?.isGeneralTermsAccepted ?? false}
                        className="px-0"
                        onChange={e => {
                            changeHandler(e, true);
                        }}
                        size={'small'}
                        id="checkbox"
                        name="isGeneralTermsAccepted"
                        color="primary"
                    />
                    <label
                        htmlFor="checkbox"
                        className="ml-2 mb-0 text-start cursor-pointer"
                        dangerouslySetInnerHTML={{ __html: t('auth.register.brand.step1.terms') }}
                    />
                </Col>

                <Col md={7} className="mt-4">
                    <StepperButtons
                        currentStep={stepIdNumber - 1}
                        handleNextStep={handleNextStep}
                        isLoading={isLoading}
                        handleBackStep={handleBackStep}
                    />
                </Col>
            </Row>
        </Form>
    );
};

export default StepOne;
